import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { AppState } from '../../reducers/root.reducer';
import AdminProjectAdminsService from '../services/AdminProjectAdminsService';
import ProjectAdminType from '../types/ProjectAdminType';
import UploadState from '../../types/UploadState';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';
import AdminUsersService from '../services/AdminUsersService';
import IBaseState from '../../types/IBaseState';
import IAdminUser from '../types/users/IAdminUser';
import { createSelector } from 'reselect';
import UserRoles from '../../common/constants/UserRoles';

const NAME = 'AdminProjectAdmins';

const getAdmins = createNotifiedThunk(
    `${NAME}/getAdmins`,
    AdminProjectAdminsService.getAdmins,
    { error: 'fe.admin.error.load-admins' },
);
const createAdmin = createNotifiedThunk(
    `${NAME}/createAdmin`,
    AdminProjectAdminsService.createAdmin,
    { error: 'fe.admin.error.load-admins' },
);
const updateAdmin = createNotifiedThunk(
    `${NAME}/updateAdmin`,
    AdminProjectAdminsService.updateAdmin,
    { error: 'fe.admin.error.load-admins' },
);
const getUsers = createNotifiedThunk(
    `${NAME}/getUsers`,
    () =>
        AdminUsersService.getUsers({ minRole: UserRoles.manager, limit: 100 }),
    { error: 'fe.admin.error.load-users' },
);

export type AdminProjectAdminsState = {
    admins: UploadState<ProjectAdminType[]>;
    users: IBaseState<IAdminUser[]>;
};

const initialState: AdminProjectAdminsState = {
    admins: { data: [], loading: false, uploading: false },
    users: { data: [], loading: false },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getAdmins.pending, (state: AdminProjectAdminsState) => {
            state.admins.loading = true;
        });
        builder.addCase(
            getAdmins.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                state.admins.data = action.payload.data;
                state.admins.loading = false;
            },
        );
        builder.addCase(
            getAdmins.rejected,
            (state: AdminProjectAdminsState) => {
                state.admins.loading = false;
            },
        );

        builder.addCase(getUsers.pending, (state: AdminProjectAdminsState) => {
            state.users.loading = true;
        });
        builder.addCase(
            getUsers.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                state.users.data = action.payload.data;
                state.users.loading = false;
            },
        );
        builder.addCase(getUsers.rejected, (state: AdminProjectAdminsState) => {
            state.users.loading = false;
        });

        builder.addCase(
            createAdmin.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                state.admins.data.push(action.payload.data);
            },
        );

        builder.addCase(
            updateAdmin.fulfilled,
            (state: AdminProjectAdminsState, action) => {
                const index = state.admins.data.findIndex(
                    (admin) => admin.id === action.meta.arg.adminId,
                );

                if (index >= 0) {
                    state.admins.data[index] = action.payload.data;
                }
            },
        );

        builder.addMatcher(
            isAnyOf(updateAdmin.pending, createAdmin.pending),
            (state: AdminProjectAdminsState) => {
                state.admins.uploading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                updateAdmin.fulfilled,
                updateAdmin.rejected,
                createAdmin.fulfilled,
                createAdmin.rejected,
            ),
            (state: AdminProjectAdminsState) => {
                state.admins.uploading = false;
            },
        );
    },
});

const state = (state: AppState): AdminProjectAdminsState => state.admin.admins;

const AdminProjectAdminsSlice = {
    reducer: slice.reducer,
    actions: {
        getAdmins,
        createAdmin,
        updateAdmin,
        getUsers,
    },
    state: {
        all: state,
        getAdmins: createSelector(
            state,
            (state: AdminProjectAdminsState) => state.admins,
        ),
        getUsers: createSelector(
            state,
            (state: AdminProjectAdminsState) => state.users,
        ),
    },
};

export default AdminProjectAdminsSlice;
